import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { GitBranch, Copy, Layers } from "lucide-react";
import {
  fetchDebateHistory,
  fetchDebateHistoryStats,
} from "../transport/authClient";
import type {
  DebateHistoryItem,
  DebateHistoryStats,
} from "../transport/authClient";
import { AppShell } from "../components/layout/AppShell";

/* ───────────────────── Helpers ───────────────────── */

function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}dk ${s}s` : `${s}s`;
}

function relativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Az önce";
  if (mins < 60) return `${mins} dk önce`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} saat önce`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} gün önce`;
  return new Date(dateStr).toLocaleDateString("tr-TR", {
    day: "numeric",
    month: "short",
  });
}

function consensusTone(score: number | null | undefined) {
  if (score == null) return { tone: "muted", color: "text-slate-500", bg: "bg-slate-500/10" };
  if (score >= 70) return { tone: "high", color: "text-emerald-400", bg: "bg-emerald-500/15" };
  if (score >= 40) return { tone: "mid", color: "text-amber-400", bg: "bg-amber-500/15" };
  return { tone: "low", color: "text-rose-400", bg: "bg-rose-500/15" };
}

/* ───────────────────── Status Badge ───────────────────── */

function StatusBadge({ status }: { status: string }) {
  const map: Record<
    string,
    { bg: string; border: string; text: string; dot: string; label: string }
  > = {
    finalized: {
      bg: "bg-emerald-500/[0.08]",
      border: "border-emerald-500/20",
      text: "text-emerald-400",
      dot: "bg-emerald-400",
      label: "Tamamlandı",
    },
    running: {
      bg: "bg-amber-500/[0.08]",
      border: "border-amber-500/20",
      text: "text-amber-400",
      dot: "bg-amber-400 animate-pulse",
      label: "Devam Ediyor",
    },
    cancelled: {
      bg: "bg-red-500/[0.08]",
      border: "border-red-500/20",
      text: "text-red-400",
      dot: "bg-red-400",
      label: "İptal",
    },
    abandoned: {
      bg: "bg-slate-500/[0.08]",
      border: "border-slate-500/20",
      text: "text-slate-400",
      dot: "bg-slate-500",
      label: "Terk Edildi",
    },
    provisioning: {
      bg: "bg-violet-500/[0.08]",
      border: "border-violet-500/20",
      text: "text-violet-400",
      dot: "bg-violet-400 animate-pulse",
      label: "Hazırlanıyor",
    },
  };
  const s = map[status] ?? {
    bg: "bg-slate-500/[0.08]",
    border: "border-slate-500/20",
    text: "text-slate-500",
    dot: "bg-slate-500",
    label: status,
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold tracking-wide uppercase border ${s.bg} ${s.border} ${s.text}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

/* ───────────────────── Sparkline ─────────────────────
   Inline SVG, no charting dep. Renders the 30-day session
   count trend as a smooth area-line. Pure-presentational. */

function Sparkline({
  values,
  color,
  height = 40,
  width = 120,
}: {
  values: number[];
  color: string;
  height?: number;
  width?: number;
}) {
  if (values.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-[10px] text-slate-600"
        style={{ height, width }}
      >
        veri yok
      </div>
    );
  }
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = Math.max(max - min, 0.001);
  const stepX = values.length > 1 ? width / (values.length - 1) : width;

  const points = values.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return [x, y] as const;
  });

  const pathD = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p[0].toFixed(2)} ${p[1].toFixed(2)}`)
    .join(" ");

  const areaD =
    `${pathD} L ${(points[points.length - 1]?.[0] ?? 0).toFixed(2)} ${height} ` +
    `L ${(points[0]?.[0] ?? 0).toFixed(2)} ${height} Z`;

  const last = points[points.length - 1];

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="overflow-visible"
    >
      <defs>
        <linearGradient id={`spark-${color}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.35} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={areaD} fill={`url(#spark-${color})`} />
      <path
        d={pathD}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {last && (
        <circle
          cx={last[0]}
          cy={last[1]}
          r={2.5}
          fill={color}
          stroke="rgb(15 23 42)"
          strokeWidth={1.5}
        />
      )}
    </svg>
  );
}

/* ───────────────────── Hero Stat Card ───────────────────── */

interface HeroStatProps {
  label: string;
  value: React.ReactNode;
  sublabel?: string;
  delta?: { value: number; suffix?: string } | null;
  spark?: { values: number[]; color: string } | null;
  icon: React.ReactNode;
  accent: "indigo" | "emerald" | "cyan" | "amber";
}

const ACCENT_MAP: Record<
  HeroStatProps["accent"],
  { ring: string; tile: string; iconColor: string }
> = {
  indigo: {
    ring: "before:bg-indigo-500/40",
    tile: "bg-indigo-500/[0.08] border-indigo-500/20",
    iconColor: "text-indigo-300",
  },
  emerald: {
    ring: "before:bg-emerald-500/40",
    tile: "bg-emerald-500/[0.08] border-emerald-500/20",
    iconColor: "text-emerald-300",
  },
  cyan: {
    ring: "before:bg-cyan-500/40",
    tile: "bg-cyan-500/[0.08] border-cyan-500/20",
    iconColor: "text-cyan-300",
  },
  amber: {
    ring: "before:bg-amber-500/40",
    tile: "bg-amber-500/[0.08] border-amber-500/20",
    iconColor: "text-amber-300",
  },
};

function HeroStat({
  label,
  value,
  sublabel,
  delta,
  spark,
  icon,
  accent,
}: HeroStatProps) {
  const c = ACCENT_MAP[accent];
  return (
    <div
      className={[
        "relative overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5",
        "transition-all duration-300 hover:border-white/[0.12] hover:bg-white/[0.04]",
        "before:absolute before:left-0 before:top-0 before:h-full before:w-[2px]",
        c.ring,
      ].join(" ")}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
            {label}
          </p>
          <div className="mt-2 flex items-baseline gap-2">
            <p className="text-3xl font-bold text-white tabular-nums tracking-tight leading-none">
              {value}
            </p>
            {delta && (
              <span
                className={[
                  "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10.5px] font-semibold",
                  delta.value > 0
                    ? "bg-emerald-500/10 text-emerald-400"
                    : delta.value < 0
                    ? "bg-rose-500/10 text-rose-400"
                    : "bg-slate-500/10 text-slate-400",
                ].join(" ")}
              >
                <svg
                  className="w-2.5 h-2.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  {delta.value > 0 ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 15l7-7 7 7"
                    />
                  ) : delta.value < 0 ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M19 9l-7 7-7-7"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 12h14"
                    />
                  )}
                </svg>
                {delta.value > 0 ? "+" : ""}
                {delta.value.toFixed(1)}
                {delta.suffix ?? "%"}
              </span>
            )}
          </div>
          {sublabel && (
            <p className="mt-1 text-[11px] text-slate-500 truncate">
              {sublabel}
            </p>
          )}
        </div>

        <div
          className={[
            "shrink-0 w-10 h-10 rounded-xl border flex items-center justify-center",
            c.tile,
            c.iconColor,
          ].join(" ")}
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            {icon}
          </svg>
        </div>
      </div>

      {spark && spark.values.length > 0 && (
        <div className="mt-3 -mb-1 -mx-1">
          <Sparkline values={spark.values} color={spark.color} width={240} height={36} />
        </div>
      )}
    </div>
  );
}

/* ───────────────────── Distribution Bar ───────────────────── */

function ConsensusDistribution({
  buckets,
}: {
  buckets: DebateHistoryStats["consensus_buckets"];
}) {
  const total = buckets.high + buckets.mid + buckets.low + buckets.none;
  if (total === 0) return null;
  const pct = (n: number) => (n / total) * 100;

  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
          Konsensüs Dağılımı
        </p>
        <span className="text-[11px] text-slate-500 font-mono">
          n = {total}
        </span>
      </div>

      <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-white/[0.04]">
        {buckets.high > 0 && (
          <div
            className="bg-gradient-to-r from-emerald-500 to-emerald-400"
            style={{ width: `${pct(buckets.high)}%` }}
            title={`Yüksek: ${buckets.high}`}
          />
        )}
        {buckets.mid > 0 && (
          <div
            className="bg-gradient-to-r from-amber-500 to-amber-400"
            style={{ width: `${pct(buckets.mid)}%` }}
            title={`Orta: ${buckets.mid}`}
          />
        )}
        {buckets.low > 0 && (
          <div
            className="bg-gradient-to-r from-rose-500 to-rose-400"
            style={{ width: `${pct(buckets.low)}%` }}
            title={`Düşük: ${buckets.low}`}
          />
        )}
        {buckets.none > 0 && (
          <div
            className="bg-slate-700"
            style={{ width: `${pct(buckets.none)}%` }}
            title={`Skor yok: ${buckets.none}`}
          />
        )}
      </div>

      <div className="mt-4 grid grid-cols-4 gap-3 text-[11px]">
        <BucketLegend
          label="Yüksek"
          hint="≥ %70"
          count={buckets.high}
          dot="bg-emerald-400"
          textColor="text-emerald-300"
        />
        <BucketLegend
          label="Orta"
          hint="%40 – 70"
          count={buckets.mid}
          dot="bg-amber-400"
          textColor="text-amber-300"
        />
        <BucketLegend
          label="Düşük"
          hint="< %40"
          count={buckets.low}
          dot="bg-rose-400"
          textColor="text-rose-300"
        />
        <BucketLegend
          label="Skor Yok"
          hint="erken iptal"
          count={buckets.none}
          dot="bg-slate-500"
          textColor="text-slate-400"
        />
      </div>
    </div>
  );
}

function BucketLegend({
  label,
  hint,
  count,
  dot,
  textColor,
}: {
  label: string;
  hint: string;
  count: number;
  dot: string;
  textColor: string;
}) {
  return (
    <div className="rounded-lg border border-white/[0.04] bg-white/[0.02] px-2.5 py-2">
      <div className="flex items-center gap-1.5">
        <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
        <span className={`font-semibold ${textColor}`}>{label}</span>
      </div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="text-base font-bold text-white tabular-nums">
          {count}
        </span>
        <span className="text-[10px] text-slate-500">{hint}</span>
      </div>
    </div>
  );
}

/* ───────────────────── Filter Tabs ───────────────────── */

// `id` follows the same conventions as the backend `?status=` query
// param — empty string means no filter. The synthetic `__drafts__` id
// activates the `include_empty=true` flag so fork/duplicate shells
// surface in their own dedicated bin instead of polluting the main
// list. Keeping this as a discriminator here means the rest of the
// store/transport layer stays unchanged.
const STATUS_TABS = [
  { id: "", label: "Tümü" },
  { id: "finalized", label: "Tamamlanan" },
  { id: "running", label: "Devam Eden" },
  { id: "provisioning", label: "Hazırlanan" },
  { id: "__drafts__", label: "Taslaklar" },
  { id: "cancelled", label: "İptal" },
] as const;

/* ───────────────────── Page ───────────────────── */

export function DashboardPage() {
  const [debates, setDebates] = useState<DebateHistoryItem[]>([]);
  const [stats, setStats] = useState<DebateHistoryStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [search, setSearch] = useState("");

  function loadList() {
    setFetchError(null);
    setLoading(true);
    // The synthetic "__drafts__" tab maps to a real backend call:
    // ?status=draft&include_empty=true — so the dashboard surfaces
    // every fork/duplicate shell the operator has lying around. Any
    // other tab keeps the default empty-draft hygiene rules.
    const isDraftsTab = statusFilter === "__drafts__";
    const realStatus = isDraftsTab ? "draft" : statusFilter;
    fetchDebateHistory({
      limit: 100,
      ...(realStatus ? { status: realStatus } : {}),
      ...(search.trim() ? { q: search.trim() } : {}),
      ...(isDraftsTab ? { include_empty: true } : {}),
    })
      .then(setDebates)
      .catch((e) =>
        setFetchError(e instanceof Error ? e.message : "Tartışmalar yüklenemedi"),
      )
      .finally(() => setLoading(false));
  }

  function loadStats() {
    setStatsLoading(true);
    fetchDebateHistoryStats()
      .then(setStats)
      .catch(() => {
        // Stats failure is non-fatal; the list still renders.
        setStats(null);
      })
      .finally(() => setStatsLoading(false));
  }

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    loadStats();
  }, []);

  // Debounced search
  useEffect(() => {
    const id = setTimeout(loadList, 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  /* ── Derived display values from stats (single source of truth) ── */

  const sparklineValues = useMemo(() => {
    if (!stats) return [];
    // Pad missing days so the sparkline always shows a full 30-day window
    const map = new Map(stats.daily_trend.map((p) => [p.date, p.count]));
    const days: number[] = [];
    const now = new Date();
    for (let i = 29; i >= 0; i--) {
      const d = new Date(now);
      d.setUTCDate(d.getUTCDate() - i);
      const key = d.toISOString().slice(0, 10);
      days.push(map.get(key) ?? 0);
    }
    return days;
  }, [stats]);

  const consensusSparkline = useMemo(() => {
    if (!stats) return [];
    const map = new Map(
      stats.daily_trend
        .filter((p) => p.avg_consensus != null)
        .map((p) => [p.date, p.avg_consensus as number]),
    );
    if (map.size === 0) return [];
    const days: number[] = [];
    const now = new Date();
    let lastKnown = 0;
    for (let i = 29; i >= 0; i--) {
      const d = new Date(now);
      d.setUTCDate(d.getUTCDate() - i);
      const key = d.toISOString().slice(0, 10);
      const v = map.get(key);
      if (v != null) lastKnown = v;
      days.push(lastKnown);
    }
    return days;
  }, [stats]);

  const completionRate = useMemo(() => {
    if (!stats || stats.total === 0) return null;
    return (stats.finalized / stats.total) * 100;
  }, [stats]);

  return (
    <AppShell>
      <main className="max-w-[1400px] mx-auto px-6 lg:px-8 py-8 space-y-8">
        {/* ─── Page Title + CTA ─── */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 shadow-[0_0_10px_rgba(99,102,241,0.6)]" />
              Karar Konseyi · Analitik
            </div>
            <h1 className="mt-2 text-3xl font-bold tracking-tight text-white">
              Tartışma Paneli
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Tüm konsensüs oturumlarınızın canlı analitiği — son 30 günlük trend, dağılım ve tamamlanma metrikleri.
            </p>
          </div>
          <Link
            to="/debate"
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 rounded-xl shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/30 transition-all duration-200 hover:-translate-y-0.5"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Yeni Tartışma
          </Link>
        </div>

        {/* ─── Hero Stats ─── */}
        {statsLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-[150px] rounded-2xl border border-white/[0.05] bg-white/[0.02] animate-pulse"
              />
            ))}
          </div>
        ) : stats ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <HeroStat
              label="Toplam Oturum"
              value={stats.total.toLocaleString("tr-TR")}
              sublabel={
                stats.week_over_week.last_week_count > 0
                  ? `Bu hafta ${Math.round(stats.week_over_week.last_week_count)}`
                  : "Bu hafta hiç başlatılmadı"
              }
              delta={
                stats.week_over_week.count_pct != null
                  ? { value: stats.week_over_week.count_pct }
                  : null
              }
              spark={{
                values: sparklineValues,
                color: "rgb(129 140 248)",
              }}
              accent="indigo"
              icon={
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M3 10h18M3 6h18M3 14h18M3 18h18"
                />
              }
            />
            <HeroStat
              label="Tamamlanma Oranı"
              value={
                completionRate != null
                  ? `%${completionRate.toFixed(0)}`
                  : "—"
              }
              sublabel={`${stats.finalized.toLocaleString("tr-TR")} / ${stats.total.toLocaleString("tr-TR")} oturum sonuca ulaştı`}
              accent="emerald"
              icon={
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              }
            />
            <HeroStat
              label="Ortalama Konsensüs"
              value={
                stats.avg_consensus != null
                  ? `%${stats.avg_consensus.toFixed(1)}`
                  : "—"
              }
              sublabel={
                stats.consensus_buckets.high > 0
                  ? `${stats.consensus_buckets.high} oturum güçlü mutabakatla bitti`
                  : "Henüz yüksek mutabakatlı oturum yok"
              }
              spark={
                consensusSparkline.length > 0
                  ? { values: consensusSparkline, color: "rgb(34 211 238)" }
                  : null
              }
              accent="cyan"
              icon={
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                />
              }
            />
            <HeroStat
              label="Ortalama Süre"
              value={formatDuration(stats.avg_duration_s)}
              sublabel={
                stats.avg_turns != null
                  ? `Oturum başına ~${stats.avg_turns} tur · toplam ${stats.total_turns.toLocaleString("tr-TR")} tur`
                  : "Tur verisi henüz yok"
              }
              accent="amber"
              icon={
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              }
            />
          </div>
        ) : null}

        {/* ─── Distribution Strip ─── */}
        {stats && stats.total > 0 && (
          <ConsensusDistribution buckets={stats.consensus_buckets} />
        )}

        {/* ─── Filter + Search Bar ─── */}
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-1.5">
            {STATUS_TABS.map((tab) => {
              const active = statusFilter === tab.id;
              return (
                <button
                  key={tab.id || "all"}
                  onClick={() => setStatusFilter(tab.id)}
                  className={[
                    "px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-all",
                    active
                      ? "bg-indigo-500/15 text-indigo-200 border border-indigo-500/30"
                      : "text-slate-400 hover:text-white border border-transparent hover:bg-white/[0.04]",
                  ].join(" ")}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
          <div className="relative w-full sm:w-72">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"
              />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Soru içinde ara…"
              className="w-full pl-9 pr-3 py-2 text-[13px] bg-white/[0.03] border border-white/[0.06] rounded-lg text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-indigo-500/50 focus:bg-white/[0.05] transition-colors"
            />
          </div>
        </div>

        {/* ─── Debate List ─── */}
        <section className="bg-white/[0.02] border border-white/[0.06] rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h2 className="text-[15px] font-semibold">Geçmiş Tartışmalar</h2>
              <span className="text-xs text-slate-500 bg-white/5 px-2 py-0.5 rounded-md font-mono">
                {debates.length}
              </span>
            </div>
            <button
              onClick={() => {
                loadList();
                loadStats();
              }}
              disabled={loading}
              className="text-xs text-slate-500 hover:text-white transition-colors flex items-center gap-1.5"
            >
              <svg
                className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Yenile
            </button>
          </div>

          {fetchError ? (
            <ErrorState message={fetchError} onRetry={loadList} />
          ) : loading ? (
            <ListSkeleton />
          ) : debates.length === 0 ? (
            <EmptyState filtered={!!(statusFilter || search)} />
          ) : (
            <div className="divide-y divide-white/[0.04]">
              {debates.map((d, i) => (
                <DebateRow
                  key={d.id}
                  debate={d}
                  index={debates.length - i}
                />
              ))}
            </div>
          )}
        </section>
      </main>
    </AppShell>
  );
}

/* ───────────────────── List Row ───────────────────── */

function VersionChip({
  derivedFrom,
  versionIndex,
  versionLabel,
  versionCount,
}: {
  derivedFrom: string;
  versionIndex: number;
  versionLabel: string | null | undefined;
  versionCount: number;
}) {
  // Standalone debates (no siblings) get NO chip — keeps the row
  // visually quiet when versioning is irrelevant. The chip only
  // appears once a fork or duplicate enters the lineage.
  if (versionCount <= 1 && derivedFrom === "original") return null;

  const isFork = derivedFrom === "fork";
  const isDup = derivedFrom === "duplicate";
  const Icon = isFork ? GitBranch : isDup ? Copy : Layers;
  const tone = isFork
    ? "bg-violet-500/10 text-violet-300 border-violet-500/30"
    : isDup
    ? "bg-cyan-500/10 text-cyan-300 border-cyan-500/30"
    : "bg-amber-500/10 text-amber-300 border-amber-500/30";
  const label = versionLabel || `v${versionIndex}`;
  const kindLabel = isFork ? "Fork" : isDup ? "Kopya" : "Orijinal";

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${tone}`}
      title={`${kindLabel} · ${label} · Bu lineage'da ${versionCount} versiyon var`}
    >
      <Icon className="h-2.5 w-2.5" aria-hidden />
      {label}
      {versionCount > 1 && (
        <span className="opacity-70 ml-0.5">/{versionCount}</span>
      )}
    </span>
  );
}

function DebateRow({
  debate: d,
  index,
}: {
  debate: DebateHistoryItem;
  index: number;
}) {
  const tone = consensusTone(d.consensus_score);
  // Empty draft = fork/duplicate shell session that was never run.
  // We route those to the wizard launcher (`/?from=<id>`) instead of
  // the read-only replay page so the operator can pick personas and
  // press Start. Otherwise a click on this row would land on a blank
  // replay screen with no escape hatch.
  const isEmptyDraft =
    d.status === "draft" && (!d.turn_count || d.turn_count === 0);
  // İş Kolu — arka planda devam eden tartışma. A `running`/`provisioning`
  // row means the engine is STILL ALIVE server-side (closing the tab no
  // longer cancels it — see backend `routes_debate.py`). Route into the
  // LIVE console and re-attach instead of the static replay report;
  // `ConsensusModePage` reads `?resumeSessionId=` on mount and calls
  // `resume(sessionId)` (see `useDiscussionStream`/`sessionSlice`).
  const isLive = d.status === "running" || d.status === "provisioning";
  const href = isEmptyDraft
    ? `/?resume=${d.id}`
    : isLive
      ? `/debate?resumeSessionId=${d.id}`
      : `/replay/${d.id}`;
  return (
    <Link
      to={href}
      className={[
        "group flex items-center gap-5 px-6 py-4 transition-all duration-150",
        isEmptyDraft
          ? "hover:bg-amber-500/[0.04]"
          : "hover:bg-white/[0.03]",
      ].join(" ")}
    >
      <div
        className={[
          "w-9 h-9 rounded-lg border flex items-center justify-center text-[11px] font-mono shrink-0 transition-colors",
          isEmptyDraft
            ? "bg-amber-500/[0.04] border-amber-500/20 text-amber-300/80 group-hover:border-amber-500/40"
            : "bg-white/[0.04] border-white/[0.06] text-slate-500 group-hover:border-indigo-500/30 group-hover:bg-indigo-500/5 group-hover:text-indigo-300",
        ].join(" ")}
      >
        {index}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <p className="text-[13.5px] font-medium text-slate-200 truncate group-hover:text-white transition-colors leading-tight">
            {d.query}
          </p>
          <VersionChip
            derivedFrom={d.derived_from ?? "original"}
            versionIndex={d.version_index ?? 1}
            versionLabel={d.version_label}
            versionCount={d.version_count ?? 1}
          />
        </div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1.5 text-[11px] text-slate-500">
          <span>{relativeTime(d.created_at)}</span>
          <span className="w-0.5 h-0.5 rounded-full bg-slate-600" />
          <span>{d.persona_count} katılımcı</span>
          {d.duration_seconds != null && (
            <>
              <span className="w-0.5 h-0.5 rounded-full bg-slate-600" />
              <span>{formatDuration(d.duration_seconds)}</span>
            </>
          )}
          {d.turn_count != null && d.turn_count > 0 && (
            <>
              <span className="w-0.5 h-0.5 rounded-full bg-slate-600" />
              <span>
                {d.turn_count} {d.turn_count === 1 ? "tur" : "tur"}
              </span>
            </>
          )}
          {d.parent_debate_id && (
            <>
              <span className="w-0.5 h-0.5 rounded-full bg-slate-600" />
              <span
                className="inline-flex items-center gap-1 text-violet-400/80"
                title="Bu tartışma bir önceki versiyondan türetildi"
              >
                <GitBranch className="h-2.5 w-2.5" aria-hidden />
                türetilmiş
              </span>
            </>
          )}
          {isEmptyDraft && (
            <>
              <span className="w-0.5 h-0.5 rounded-full bg-slate-600" />
              <span
                className="inline-flex items-center gap-1 text-amber-300/90 font-medium"
                title="Bu tartışma henüz başlatılmadı — tıklayıp wizardı açabilirsin"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400/70 animate-pulse" />
                Henüz başlatılmadı
              </span>
            </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4 shrink-0">
        {isEmptyDraft ? (
          <span className="px-2.5 py-1 rounded-md text-[11px] font-semibold bg-amber-500/10 text-amber-300 ring-1 ring-amber-500/30 group-hover:bg-amber-500/20">
            Tartışmayı Başlat →
          </span>
        ) : d.consensus_score != null ? (
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider text-slate-600 mb-0.5 font-semibold">
              Konsensüs
            </p>
            <div
              className={[
                "px-2 py-0.5 rounded-md inline-flex items-center gap-1.5",
                tone.bg,
              ].join(" ")}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${tone.color.replace("text-", "bg-")}`} />
              <span className={`text-[13px] font-bold font-mono ${tone.color}`}>
                %{d.consensus_score.toFixed(0)}
              </span>
            </div>
          </div>
        ) : (
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider text-slate-600 mb-0.5 font-semibold">
              Konsensüs
            </p>
            <span className="text-[12px] text-slate-600 font-mono">—</span>
          </div>
        )}
        <StatusBadge status={d.status} />
        <svg
          className="w-4 h-4 text-slate-600 group-hover:text-slate-300 transition-colors"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </div>
    </Link>
  );
}

/* ───────────────────── States ───────────────────── */

function ListSkeleton() {
  return (
    <div className="divide-y divide-white/[0.04]">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="flex items-center gap-5 px-6 py-4 animate-pulse">
          <div className="w-9 h-9 rounded-lg bg-white/[0.05]" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-2/3 rounded bg-white/[0.06]" />
            <div className="h-2 w-1/3 rounded bg-white/[0.04]" />
          </div>
          <div className="h-6 w-16 rounded bg-white/[0.04]" />
          <div className="h-6 w-20 rounded bg-white/[0.04]" />
        </div>
      ))}
    </div>
  );
}

function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="p-16 text-center">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-500/10 mb-4">
        <svg
          className="w-5 h-5 text-red-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
      </div>
      <p className="text-red-300 text-sm font-medium mb-1">Yükleme hatası</p>
      <p className="text-slate-500 text-xs mb-4">{message}</p>
      <button
        onClick={onRetry}
        className="text-xs text-indigo-400 hover:text-indigo-300 font-medium"
      >
        Tekrar dene
      </button>
    </div>
  );
}

function EmptyState({ filtered }: { filtered: boolean }) {
  return (
    <div className="p-16 text-center">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500/10 to-violet-500/10 border border-indigo-500/20 mb-4">
        <svg
          className="w-6 h-6 text-indigo-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
      </div>
      <p className="text-white font-medium mb-1">
        {filtered ? "Eşleşen oturum yok" : "Henüz tartışma yok"}
      </p>
      <p className="text-slate-500 text-sm mb-5">
        {filtered
          ? "Filtreyi gevşetip tekrar deneyin."
          : "İlk konsensüs oturumunuzu başlatarak keşfedin."}
      </p>
      {!filtered && (
        <Link
          to="/debate"
          className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-indigo-600 hover:bg-indigo-500 rounded-xl transition-colors"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          İlk Tartışmayı Başlat
        </Link>
      )}
    </div>
  );
}
